<script setup lang="ts">
  import { computed, ref } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import HTMLRenderer from '@/components/common/HTMLRenderer.vue'

  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useShopifyClient from '@/composables/useShopifyClient'
  import useSegment from '@/composables/useSegment'

  import { UserModule } from '@/store/modules/user'

  const { mediaIcon, arrowRightIndicator } = useIcons()
  const { courseBundle } = useCourse()
  const { handleShopifyCheckout } = useShopifyClient()
  const { coursePackagePurchaseIntended } = useSegment()

  const isLoading = ref(false)

  const totalCoursePrice = computed(() => {
    return courseBundle.value?.courses?.reduce((total, course) => total + (course.shopifyProductPrice || 0), 0)
  })

  const buyNow = () => {
    coursePackagePurchaseIntended(courseBundle.value!.courseBundleId, courseBundle.value!.title, 'bundle_upsell')
    isLoading.value = true

    handleShopifyCheckout(courseBundle.value!.shopifyProductVariantId!, UserModule.email || null)

    setTimeout(() => {
      isLoading.value = false
    }, 1000)
  }
</script>

<template lang="pug">
.buy-package-details-wrapper
  .package-details-image
    img(:src='courseBundle?.teaserImageUrl')
  .buy-package-details
    .buy-package-title-wrapper
      h2.ketch-font-bold {{ $t('courses.writtenAndOral') }}
      h3 {{ $t('everythingIncluded') }}
    .content-separator.ketch-w-c100
    .package-benefits
      .benefit-item(v-for='benefit in courseBundle?.bundleBenefits', :key='benefit')
        .media-icon-wrapper
          SVGRenderer(
            :icon='mediaIcon',
            :stroke-color='"var(--primary-text-color)"',
            :has-hover='false',
            width='30px',
            height='30px'
          )
        HTMLRenderer.h5(:html='benefit', link-target='_blank')
    .buy-package-cta-wrapper
      .package-price-details
        .package-price
          h2.discounted-price {{ courseBundle?.shopifyProductPrice }}€
          h3.original-price {{ totalCoursePrice }}€
        h5.ketch-leading-none {{ $t('vatFree') }}
      KetchUpButton.primary(@click.native='buyNow')
        LoadingSpinner(v-if='isLoading')
        template(v-else)
          h4 {{ $t('courses.buyPackage') }}
          SVGRenderer(
            :icon='arrowRightIndicator',
            :fill-color='"var(--primary-foreground-color)"',
            :has-hover='false',
            width='15px',
            height='12px'
          )
</template>

<style lang="postcss" scoped>
  .buy-package-details-wrapper {
    @apply ketch-flex ketch-flex-col ketch-space-y-c15 ketch-bg-content-background-color ketch-p-c10;
    @apply ketch-rounded-normal ketch-h-full ketch-shadow-content-shadow;
    @apply xs1:ketch-p-c15 sm:ketch-rounded-large sm:ketch-p-c20 sm:ketch-space-y-0 sm:ketch-grid sm:ketch-grid-cols-2 sm:ketch-gap-c20;

    .package-details-image {
      @apply ketch-order-first sm:ketch-order-last sm:ketch-h-full;

      img {
        @apply ketch-w-full ketch-object-cover ketch-rounded-normal;
      }
    }

    .buy-package-details {
      @apply ketch-flex ketch-flex-col ketch-space-y-c15;

      .package-benefits {
        @apply ketch-flex ketch-flex-col ketch-space-y-c10;

        .benefit-item {
          @apply ketch-flex ketch-items-center ketch-space-x-c10;

          .media-icon-wrapper {
            @apply ketch-w-c30 ketch-h-c30 ketch-flex-shrink-0;
          }

          h5 {
            @apply ketch-m-0;
          }
        }
      }

      .buy-package-cta-wrapper {
        @apply ketch-flex ketch-items-center ketch-justify-between;

        .package-price-details {
          @apply ketch-flex ketch-flex-col;

          .package-price {
            @apply ketch-flex ketch-items-center ketch-space-x-c5;

            h2.discounted-price {
              @apply ketch-font-bold ketch-text-[#E11D48];
            }

            h3.original-price {
              @apply ketch-line-through;
            }

            .discounted-price,
            .original-price {
              @apply ketch-leading-none;
            }
          }
        }

        button {
          @apply ketch-w-auto ketch-px-c10 sm:ketch-px-c20 sm:ketch-w-[180px] lg:ketch-w-[200px];
          filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.15));

          h4 {
            @apply ketch-font-bold;
          }
        }
      }
    }
  }
</style>
